import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, Ban, Pencil, X } from "lucide-react";
import { api, apiErr, Badge, inputCls, labelCls } from "./adminShared";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const emptyBlock = { date_start: "", date_end: "", all_day: false, start_time: "08:00", end_time: "18:00", full_crew: true, cleaners: 1, reason: "" };

function BlockModal({ block, onClose, onSaved }) {
  const [f, setF] = useState(block?.block_id ? { ...block, date_end: block.date_end || "" } : { ...emptyBlock });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.date_start) { toast.error("Start date is required"); return; }
    setSaving(true);
    const payload = { ...f, date_end: f.date_end || null, cleaners: Number(f.cleaners) || 1 };
    try {
      if (block?.block_id) await api.put(`/admin/blocks/${block.block_id}`, payload);
      else await api.post("/admin/blocks", payload);
      toast.success(block?.block_id ? "Block updated" : "Blocked time added");
      onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="block-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h3 className="font-serif text-2xl text-[#2C2621]">{block?.block_id ? "Edit blocked time" : "Block time"}</h3>
          <button onClick={onClose} data-testid="block-modal-close"><X className="text-[#635B52]" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><p className={labelCls}>Start date</p><input type="date" value={f.date_start} onChange={(e) => set("date_start", e.target.value)} className={inputCls} data-testid="block-date-start" /></div>
            <div><p className={labelCls}>End date (optional)</p><input type="date" value={f.date_end} onChange={(e) => set("date_end", e.target.value)} className={inputCls} data-testid="block-date-end" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#2C2621]"><input type="checkbox" checked={f.all_day} onChange={(e) => set("all_day", e.target.checked)} data-testid="block-all-day" /> All day</label>
          {!f.all_day && (
            <div className="grid grid-cols-2 gap-2">
              <div><p className={labelCls}>Start time</p><input type="time" value={f.start_time} onChange={(e) => set("start_time", e.target.value)} className={inputCls} data-testid="block-start-time" /></div>
              <div><p className={labelCls}>End time</p><input type="time" value={f.end_time} onChange={(e) => set("end_time", e.target.value)} className={inputCls} data-testid="block-end-time" /></div>
            </div>
          )}
          <div>
            <p className={labelCls}>Cleaners unavailable</p>
            <select value={f.full_crew ? "crew" : "one"} onChange={(e) => set("full_crew", e.target.value === "crew")} className={inputCls} data-testid="block-crew">
              <option value="crew">Entire crew (blocks all bookings)</option>
              <option value="one">1 cleaner (a 1-cleaner job can still overlap)</option>
            </select>
          </div>
          <div><p className={labelCls}>Reason / private note</p><input value={f.reason} onChange={(e) => set("reason", e.target.value)} placeholder="e.g. Holiday, staff PTO" className={inputCls} data-testid="block-reason" /></div>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={save} disabled={saving} className="flex-1 bg-[#B87333] text-white rounded-full py-2.5 text-sm font-medium disabled:opacity-60" data-testid="block-save">{saving ? "Saving…" : "Save block"}</button>
          <button onClick={onClose} className="px-5 border border-[#E6DEC3] rounded-full text-sm text-[#635B52]">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCalendar() {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [blockModal, setBlockModal] = useState(null); // null | {} | block

  const load = () => {
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const byDate = useMemo(() => {
    const m = {};
    bookings.forEach((b) => { if (b.status !== "cancelled") (m[b.date] = m[b.date] || []).push(b); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return m;
  }, [bookings]);
  const blocksOn = (dstr) => blocks.filter((bl) => bl.date_start <= dstr && (bl.date_end || bl.date_start) >= dstr);

  const move = (dir) => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + 7 * dir);
    else d.setDate(d.getDate() + dir);
    setCursor(d);
  };
  const title = view === "month" ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : view === "week" ? `Week of ${startOfWeek(cursor).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
    : cursor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  const deleteBlock = async (id) => {
    if (!window.confirm("Remove this blocked time?")) return;
    try { await api.delete(`/admin/blocks/${id}`); toast.success("Block removed"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const DayCell = ({ d, compact }) => {
    const dstr = ymd(d);
    const items = byDate[dstr] || [];
    const bl = blocksOn(dstr);
    const isToday = ymd(new Date()) === dstr;
    return (
      <div className={`min-h-[92px] p-2 border border-[#E6DEC3] ${compact ? "" : "rounded-lg"} ${isToday ? "bg-[#FBF6EE]" : "bg-white"}`} data-testid={`cal-day-${dstr}`}>
        <div className="flex items-center justify-between">
          <span className={`text-xs font-semibold ${isToday ? "text-[#B87333]" : "text-[#635B52]"}`}>{d.getDate()}</span>
          {bl.length > 0 && <Ban size={12} className="text-red-500" title="Blocked time" />}
        </div>
        <div className="mt-1 space-y-1">
          {bl.map((b) => (
            <div key={b.block_id} className="text-[10px] leading-tight bg-red-50 text-red-700 rounded px-1 py-0.5 truncate" title={b.reason}>
              {b.all_day ? "All day" : `${b.start_time}-${b.end_time}`} · {b.full_crew ? "crew" : "1"}
            </div>
          ))}
          {items.slice(0, compact ? 6 : 3).map((b) => (
            <div key={b.booking_id} className="text-[10px] leading-tight bg-[#F5F0E8] text-[#2C2621] rounded px-1 py-0.5 truncate" title={`${b.service_name} — ${b.customer_name}`}>
              {b.start_time} {b.customer_name?.split(" ")[0]} <span className="text-[#B87333]">({b.cleaners}c)</span>
            </div>
          ))}
          {!compact && items.length > 3 && <div className="text-[10px] text-[#635B52]">+{items.length - 3} more</div>}
        </div>
      </div>
    );
  };

  const monthGrid = () => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    return (
      <div data-testid="cal-month">
        <div className="grid grid-cols-7 gap-1 mb-1">{WD.map((w) => <div key={w} className="text-center text-xs font-medium text-[#635B52] py-1">{w}</div>)}</div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => <div key={i} className={d.getMonth() !== cursor.getMonth() ? "opacity-40" : ""}><DayCell d={d} compact /></div>)}
        </div>
      </div>
    );
  };
  const weekGrid = () => {
    const s = startOfWeek(cursor);
    const days = Array.from({ length: 7 }, (_, i) => addDays(s, i));
    return (
      <div className="grid grid-cols-1 sm:grid-cols-7 gap-1" data-testid="cal-week">
        {days.map((d, i) => (
          <div key={i}>
            <p className="text-xs font-medium text-[#635B52] mb-1">{WD[d.getDay()]} {d.getDate()}</p>
            <DayCell d={d} compact />
          </div>
        ))}
      </div>
    );
  };
  const dayView = () => {
    const dstr = ymd(cursor);
    const items = byDate[dstr] || [];
    const bl = blocksOn(dstr);
    return (
      <div className="space-y-2" data-testid="cal-day-view">
        {bl.map((b) => (
          <div key={b.block_id} className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl p-3">
            <div className="text-sm text-red-700"><Ban size={14} className="inline mr-1" />Blocked {b.all_day ? "all day" : `${b.start_time}–${b.end_time}`} · {b.full_crew ? "entire crew" : "1 cleaner"}{b.reason ? ` — ${b.reason}` : ""}</div>
            <div className="flex gap-1">
              <button onClick={() => setBlockModal(b)} className="text-[#635B52] p-1" data-testid={`block-edit-${b.block_id}`}><Pencil size={14} /></button>
              <button onClick={() => deleteBlock(b.block_id)} className="text-red-600 p-1" data-testid={`block-del-${b.block_id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {items.map((b) => (
          <div key={b.booking_id} className="flex items-center justify-between bg-white border border-[#E6DEC3] rounded-xl p-3">
            <div>
              <p className="text-sm font-medium text-[#2C2621]">{b.start_time} CT · {b.service_name} <span className="text-[#B87333]">({b.cleaners} cleaner{b.cleaners > 1 ? "s" : ""})</span></p>
              <p className="text-xs text-[#635B52]">{b.customer_name} · {b.tier_label} · {b.duration_minutes} min</p>
            </div>
            <Badge s={b.status} />
          </div>
        ))}
        {!items.length && !bl.length && <p className="text-center text-[#635B52] py-10">Nothing scheduled.</p>}
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => move(-1)} className="p-2 rounded-full border border-[#E6DEC3] bg-white" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <button onClick={() => move(1)} className="p-2 rounded-full border border-[#E6DEC3] bg-white" data-testid="cal-next"><ChevronRight size={16} /></button>
          <button onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setCursor(d); }} className="px-3 py-1.5 rounded-full border border-[#E6DEC3] bg-white text-sm text-[#635B52]" data-testid="cal-today">Today</button>
          <h2 className="font-serif text-xl text-[#2C2621] ml-1">{title}</h2>
        </div>
        <div className="flex gap-2">
          <div className="flex gap-1 bg-white border border-[#E6DEC3] rounded-full p-1">
            {["day", "week", "month"].map((v) => (
              <button key={v} onClick={() => setView(v)} className={`rounded-full px-3 py-1.5 text-sm capitalize ${view === v ? "bg-[#2C2621] text-white" : "text-[#635B52]"}`} data-testid={`cal-view-${v}`}>{v}</button>
            ))}
          </div>
          <button onClick={() => setBlockModal({})} className="inline-flex items-center gap-1 bg-[#B87333] text-white rounded-full px-4 py-2 text-sm" data-testid="cal-add-block"><Plus size={14} /> Block time</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E6DEC3] p-3 sm:p-4">
        {view === "month" && monthGrid()}
        {view === "week" && weekGrid()}
        {view === "day" && dayView()}
      </div>

      <div className="mt-5">
        <p className={labelCls}>All blocked time</p>
        <div className="mt-2 space-y-2" data-testid="blocks-list">
          {blocks.map((b) => (
            <div key={b.block_id} className="flex items-center justify-between bg-white border border-[#E6DEC3] rounded-xl p-3 text-sm">
              <div className="text-[#2C2621]"><Ban size={14} className="inline mr-1 text-red-500" />{b.date_start}{b.date_end && b.date_end !== b.date_start ? `–${b.date_end}` : ""} · {b.all_day ? "All day" : `${b.start_time}–${b.end_time}`} · {b.full_crew ? "entire crew" : "1 cleaner"}{b.reason ? ` — ${b.reason}` : ""}</div>
              <div className="flex gap-1">
                <button onClick={() => setBlockModal(b)} className="text-[#635B52] p-1"><Pencil size={14} /></button>
                <button onClick={() => deleteBlock(b.block_id)} className="text-red-600 p-1"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {!blocks.length && <p className="text-sm text-[#635B52]">No blocked time set.</p>}
        </div>
      </div>

      {blockModal !== null && <BlockModal block={blockModal} onClose={() => setBlockModal(null)} onSaved={load} />}
    </div>
  );
}
